<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import MessageApi from 'dashboard/api/inbox/message';
import Avatar from 'next/avatar/Avatar.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const SNIPPET_LENGTH = 120;

const store = useStore();
const { t } = useI18n();
const { getPlainText, highlightContent } = useMessageFormatter();

const [isOpen, toggleOpen] = useToggle(false);
const searchInput = ref(null);
const listRef = ref(null);
const query = ref('');
const results = ref([]);
const isLoading = ref(false);
const hasSearched = ref(false);
const activeIndex = ref(-1);
const jumpedId = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);
const conversationId = computed(() => currentChat.value?.id);

const trimmedQuery = computed(() => query.value.trim());
const showMinCharsHint = computed(
  () => trimmedQuery.value.length < MIN_QUERY_LENGTH
);
const showEmptyState = computed(
  () =>
    hasSearched.value &&
    !isLoading.value &&
    !showMinCharsHint.value &&
    !results.value.length
);

const footerText = computed(() => {
  if (jumpedId.value && activeIndex.value >= 0) {
    return t('CONVERSATION.SEARCH_PANEL.RESULT_OF', {
      current: activeIndex.value + 1,
      total: results.value.length,
    });
  }
  return t('CONVERSATION.SEARCH_PANEL.RESULTS_COUNT', results.value.length);
});

const scrollActiveIntoView = () => {
  const el = listRef.value?.querySelector(
    `[data-result-index="${activeIndex.value}"]`
  );
  el?.scrollIntoView({ block: 'nearest' });
};

let requestToken = 0;
const runSearch = async term => {
  requestToken += 1;
  const token = requestToken;
  isLoading.value = true;
  try {
    const { data } = await MessageApi.search({
      conversationId: conversationId.value,
      q: term,
    });
    if (token !== requestToken) return;
    // Finder returns newest-first; keep that order (newest match at the top)
    // so the first row is the most recent and ↓ steps back through history.
    results.value = [...(data?.payload || [])].sort(
      (a, b) => b.created_at - a.created_at
    );
    hasSearched.value = true;
    activeIndex.value = 0;
    jumpedId.value = null;
    await nextTick();
    scrollActiveIntoView();
  } catch {
    if (token !== requestToken) return;
    results.value = [];
    hasSearched.value = true;
  } finally {
    if (token === requestToken) isLoading.value = false;
  }
};

let debounceTimer = null;
watch(query, () => {
  clearTimeout(debounceTimer);
  jumpedId.value = null;
  if (showMinCharsHint.value) {
    results.value = [];
    hasSearched.value = false;
    activeIndex.value = -1;
    return;
  }
  debounceTimer = setTimeout(() => runSearch(trimmedQuery.value), DEBOUNCE_MS);
});

const openPanel = async () => {
  toggleOpen(true);
  await nextTick();
  searchInput.value?.focus();
  searchInput.value?.select();
};

const closePanel = () => toggleOpen(false);
const togglePanel = () => (isOpen.value ? closePanel() : openPanel());

watch(conversationId, () => {
  query.value = '';
  results.value = [];
  hasSearched.value = false;
  activeIndex.value = -1;
  jumpedId.value = null;
  closePanel();
});

// Same routine as ConversationCard.jumpToMatchedMessage: if the target
// predates the loaded window, one ranged fetch (target-inclusive) pulls the
// gap, then the scroll bus event finds its #message<id> element.
const jumpToResult = async index => {
  const message = results.value[index];
  if (!message) return;
  activeIndex.value = index;
  jumpedId.value = message.id;
  const messageId = Number(message.id);
  const { id: chatId, messages = [] } = currentChat.value || {};
  const isLoaded = messages.some(m => Number(m.id) === messageId);
  if (!isLoaded && messages.length && Number(messages[0].id) > messageId) {
    await store.dispatch('fetchPreviousMessages', {
      conversationId: chatId,
      after: messageId,
      before: messages[0].id,
    });
  }
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
};

const onInputKeydown = e => {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    if (!results.value.length) return;
    const delta = e.key === 'ArrowDown' ? 1 : -1;
    activeIndex.value = Math.min(
      Math.max(activeIndex.value + delta, 0),
      results.value.length - 1
    );
    scrollActiveIntoView();
  } else if (e.key === 'Enter') {
    e.preventDefault();
    if (activeIndex.value >= 0) jumpToResult(activeIndex.value);
  } else if (e.key === 'Escape') {
    e.stopPropagation();
    closePanel();
  }
};

useKeyboardEvents({
  'Alt+KeyF': {
    action: e => {
      e.preventDefault();
      openPanel();
    },
    allowOnFocusedInput: true,
  },
});

const senderName = message =>
  message.sender?.name || t('CONVERSATION.SEARCH_PANEL.BOT');
const timeAgo = message => dynamicTime(message.created_at);
const isOutgoing = message => message.message_type === MESSAGE_TYPE.OUTGOING;

const snippetHtml = message => {
  const term = trimmedQuery.value;
  const plain = getPlainText(message.content || '');
  const matchAt = plain.toLowerCase().indexOf(term.toLowerCase());
  // Centre the window on the match so the highlight survives truncation.
  const start = matchAt > 40 ? matchAt - 25 : 0;
  let snippet = plain.slice(start, start + SNIPPET_LENGTH);
  if (start > 0) snippet = `…${snippet}`;
  if (start + SNIPPET_LENGTH < plain.length) snippet = `${snippet}…`;
  return highlightContent(snippet, term, 'conv-search-hit');
};
</script>

<template>
  <div v-on-clickaway="closePanel" class="relative flex items-center">
    <ButtonV4
      v-tooltip="$t('CONVERSATION.HEADER.SEARCH_MESSAGES')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-search"
      class="rounded-md"
      @click="togglePanel"
    />
    <div
      v-if="isOpen"
      class="absolute top-full z-50 mt-1 flex w-[min(26.875rem,calc(100vw-2rem))] flex-col gap-2 rounded-xl bg-n-alpha-3 p-2 shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px] ltr:right-0 rtl:left-0"
    >
      <div class="relative">
        <span
          class="i-lucide-search absolute top-2.5 size-3.5 text-n-slate-11 ltr:left-3 rtl:right-3"
        />
        <input
          ref="searchInput"
          v-model="query"
          type="search"
          :placeholder="$t('CONVERSATION.SEARCH_PANEL.PLACEHOLDER')"
          class="reset-base h-8 w-full rounded-lg border-none bg-n-alpha-black2 py-2 text-sm text-n-slate-12 focus:outline-none dark:bg-n-solid-1 ltr:pl-10 ltr:pr-2 rtl:pl-2 rtl:pr-10"
          @keydown="onInputKeydown"
        />
      </div>
      <div v-if="isLoading" class="flex items-center justify-center py-3">
        <Spinner :size="24" />
      </div>
      <div
        v-else-if="showMinCharsHint"
        class="px-2 py-1.5 text-sm text-n-slate-11"
      >
        {{ $t('CONVERSATION.SEARCH_PANEL.MIN_CHARS') }}
      </div>
      <div
        v-else-if="showEmptyState"
        class="px-2 py-1.5 text-sm text-n-slate-11"
      >
        {{ $t('CONVERSATION.SEARCH_PANEL.NO_RESULTS') }}
      </div>
      <template v-else-if="results.length">
        <div ref="listRef" class="flex max-h-72 flex-col overflow-y-auto">
          <button
            v-for="(message, index) in results"
            :key="message.id"
            type="button"
            :data-result-index="index"
            class="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-1 dark:bg-n-solid-active': index === activeIndex,
            }"
            @click="jumpToResult(index)"
          >
            <Avatar
              :name="senderName(message)"
              :src="message.sender?.thumbnail"
              :size="20"
              rounded-full
              class="mt-0.5 flex-shrink-0"
            />
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-2">
                <span class="truncate text-xs font-medium text-n-slate-12">
                  {{ senderName(message) }}
                </span>
                <span
                  class="flex flex-shrink-0 items-center gap-1 text-xs text-n-slate-10"
                >
                  <span
                    v-if="message.private"
                    class="rounded bg-n-amber-3 px-1 text-n-amber-11 dark:bg-n-amber-11/15"
                  >
                    {{ $t('CONVERSATION.SEARCH_PANEL.NOTE') }}
                  </span>
                  <span
                    v-else-if="isOutgoing(message)"
                    class="rounded bg-n-alpha-1 px-1 dark:bg-n-alpha-2"
                  >
                    {{ $t('CONVERSATION.SEARCH_PANEL.YOU') }}
                  </span>
                  {{ timeAgo(message) }}
                </span>
              </div>
              <p
                v-dompurify-html="snippetHtml(message)"
                class="mb-0 truncate text-sm text-n-slate-11 [&_.conv-search-hit]:rounded-[3px] [&_.conv-search-hit]:bg-n-amber-4 [&_.conv-search-hit]:font-semibold [&_.conv-search-hit]:text-n-amber-12 dark:[&_.conv-search-hit]:bg-n-amber-11/30 dark:[&_.conv-search-hit]:text-n-amber-4"
              />
            </div>
          </button>
        </div>
        <div
          class="flex items-center justify-between border-t border-n-weak px-2 pt-2 text-xs text-n-slate-11"
        >
          <span>{{ footerText }}</span>
          <span class="hidden sm:inline">
            {{ $t('CONVERSATION.SEARCH_PANEL.NAVIGATE_HINT') }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
